import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { Loader2, Pencil, Trash2, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useIsAdmin, useCurrentUser } from "@/hooks/useCurrentUser";
import { trackPageView } from "@/lib/analytics";
import {
  settingsKeys,
  useCreateUser,
  useDeleteUser,
  useUpdateUser,
  useUserRoles,
  useUsers,
  useBusinesses,
  useMeetingTypes,
  useProfileSearch
} from "@/services/settings";
import { apiGet } from "@/lib/apiClient";
import { cn } from "@/utils/cn";
import {
  BusinessMeetingTypeSelector,
  type BusinessMeetingTypeMappingRow
} from "./BusinessMeetingTypeSelector";
import { EmailLookupAutocomplete } from "./EmailLookupAutocomplete";
import type { UserResponse } from "@/types/settings";

const schema = z.object({
  userName: z.string().min(1, "User name is required"),
  email: z.string().email("Valid email is required"),
  firstName: z.string(),
  lastName: z.string(),
  // Rejects empty string AND the placeholder "0" value
  roleId: z.string().refine((v) => v !== "" && v !== "0", "Role is required")
});

type FormValues = z.infer<typeof schema>;

type SortKey =
  | "userName"
  | "email"
  | "roleName"
  | "businessNameCSV"
  | "meetingTypesNameCSV"
  | "createdDate"
  | "lastLoginDate";

type SortDirection = "asc" | "desc";
type SortState = Record<SortKey, SortDirection | null>;

const SORT_COLUMN_ORDER: SortKey[] = [
  "userName",
  "email",
  "roleName",
  "businessNameCSV",
  "meetingTypesNameCSV",
  "createdDate",
  "lastLoginDate"
];

function displayDate(value: string | null) {
  if (!value) return "-";
  try {
    return format(parseISO(value), "MM-dd-yyyy");
  } catch {
    return "-";
  }
}

function SortableHeader({
  label,
  sortDirection,
  onClick
}: Readonly<{
  label: string;
  sortDirection: SortDirection | null;
  onClick: () => void;
}>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full inline-flex items-center gap-1 px-1 py-1 hover:bg-slate-300 transition-colors cursor-pointer select-none"
      title={`Click to sort by ${label}. Current: ${sortDirection ? sortDirection.toUpperCase() : "None"}`}
    >
      <span>{label}</span>
      {sortDirection ? (
        <span className="text-xs font-bold" aria-label={`Sorted ${sortDirection}`}>
          {sortDirection === "asc" ? "↑" : "↓"}
        </span>
      ) : (
        <span className="text-xs text-slate-400 opacity-50">⇅</span>
      )}
    </button>
  );
}

export function UserManagementPage() {
  const currentUser = useCurrentUser();
  const userId = currentUser?.email ?? "Unknown";

  // Track page view on mount
  useEffect(() => {
    trackPageView("User Management", userId);
  }, [userId]);

  const [editing, setEditing] = useState<UserResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [mappings, setMappings] = useState<BusinessMeetingTypeMappingRow[]>([]);
  const [emailLookupText, setEmailLookupText] = useState("");
  const [businessError, setBusinessError] = useState<string>("");
 // State is stored locally, need to wire this up to API Calls
  const [dashboardAccess, setDashboardAccess] = useState<Record<string, boolean>>({});
  const [sortState, setSortState] = useState<SortState>({
    userName: null,
    email: null,
    roleName: null,
    businessNameCSV: null,
    meetingTypesNameCSV: null,
    createdDate: null,
    lastLoginDate: null
  });

  const queryClient = useQueryClient();
  // API-driven permission check: derives admin status from the user permission API response
  const isUserAdmin = useIsAdmin();
  const { data: users, isPending, isFetching } = useUsers();
  const { data: roles = [] } = useUserRoles();
  const { data: businesses = [] } = useBusinesses();
  const { data: meetingTypes = [] } = useMeetingTypes();
  const { data: profileSuggestions = [], isFetching: searchingProfiles } =
    useProfileSearch(editing?.id === 0 ? emailLookupText : "");
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const deleteMutation = useDeleteUser();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      userName: "",
      email: "",
      firstName: "",
      lastName: "",
      roleId: ""
    }
  });

  // Mirrors Blazor ShouldShowBusinessSection:
  // only show for Program Owner / Program Reviewer roles
  const watchedRoleId = form.watch("roleId");
  const showBusinessSection = useMemo(() => {
    if (!watchedRoleId) return false;
    const rid = parseInt(watchedRoleId, 10);
    const found = roles.find((r) => r.id === rid);
    return (
      found?.roleCode === "business_approver" ||
      found?.roleCode === "business_reviewer"
    );
  }, [watchedRoleId, roles]);

  useEffect(() => {
    const isAddMode = editing?.id === 0;
    if (!isAddMode || !showBusinessSection || meetingTypes.length === 0 || mappings.length === 0) {
      return;
    }

    setMappings((prev) => {
      const needsDefault = prev.some((row) => row.meetingTypes.length === 0);
      if (!needsDefault) return prev;
      return prev.map((row) =>
        row.meetingTypes.length === 0 ? { ...row, meetingTypes: [...meetingTypes] } : row
      );
    });
  }, [editing?.id, showBusinessSection, meetingTypes, mappings.length]);

  const filteredUsers = useMemo(() => {
    const all = users ?? [];
    const q = searchTerm.trim().toLowerCase();
    if (!q) return all;

    return all.filter((user) => {
      const userName = user.userName?.toLowerCase() ?? "";
      const email = user.email?.toLowerCase() ?? "";
      return userName.includes(q) || email.includes(q);
    });
  }, [searchTerm, users]);

  const activeSorts = useMemo(() => {
    return SORT_COLUMN_ORDER.flatMap((col) => {
      const dir = sortState[col];
      return dir === null ? [] : [{ key: col, direction: dir }];
    });
  }, [sortState]);

  const sortedUsers = useMemo(() => {
    if (activeSorts.length === 0) return filteredUsers;

    return [...filteredUsers].sort((a, b) => {
      for (const sort of activeSorts) {
        let aValue: string | number;
        let bValue: string | number;

        switch (sort.key) {
          case "createdDate":
          case "lastLoginDate": {
            const aDate = a[sort.key];
            const bDate = b[sort.key];
            aValue = aDate ? parseISO(aDate).getTime() : Number.NEGATIVE_INFINITY;
            bValue = bDate ? parseISO(bDate).getTime() : Number.NEGATIVE_INFINITY;
            break;
          }
          default:
            aValue = (a[sort.key] ?? "").toString().toLowerCase();
            bValue = (b[sort.key] ?? "").toString().toLowerCase();
        }

        if (aValue < bValue) return sort.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sort.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredUsers, activeSorts]);

  const handleColumnSort = (key: SortKey) => {
    setSortState((prev) => {
      const current = prev[key];
      let next: SortDirection | null;
      if (current === null) { next = "asc"; }
      else if (current === "asc") { next = "desc"; }
      else { next = null; }
      return { ...prev, [key]: next };
    });
  };

  const getAriaSort = (key: SortKey): "none" | "ascending" | "descending" => {
    const dir = sortState[key];
    if (!dir) return "none";
    return dir === "asc" ? "ascending" : "descending";
  };

  const openCreate = () => {
    setEditing({
      id: 0,
      isLoggedInUser: false,
      email: "",
      name: "",
      firstName: "",
      lastName: "",
      userName: "",
      roleId: null,
      roleName: "",
      roleCode: "",
      createdDate: null,
      lastLoginDate: null,
      businessNameCSV: "",
      meetingTypesNameCSV: "",
      businesses: {},
      permissions: [],
      specialPermissions: [],
      roles:null
    });
    setMappings([
      {
        businessName: "",
        meetingTypes: [...meetingTypes]
      }
    ]);
    setEmailLookupText("");
    setBusinessError("");
    setDashboardAccess({
      CanViewDashboard: true,
      CanViewGRCDashboard: false
    });
    form.reset({
      userName: "",
      email: "",
      firstName: "",
      lastName: "",
      roleId: ""
    });
  };

  const openEdit = async (user: UserResponse) => {
    setEditing(user);

    // Fetch full user data (with permissions) from the single-user endpoint
    const fullUser = await queryClient.fetchQuery({
      queryKey: settingsKeys.userByEmail(user.email ?? ""),
      queryFn: () => apiGet<UserResponse>(`user/${encodeURIComponent(user.email ?? "")}`),
      staleTime: 0
    });
    const editDefaults: Record<string, boolean> = {};
    for (const sp of fullUser?.specialPermissions ?? []) {
      editDefaults[sp.componentKey] = sp.isActive;
    }
    setDashboardAccess(editDefaults);
    const preloadedFromMap: BusinessMeetingTypeMappingRow[] = Object.entries(user.businesses ?? {})
      .map(([businessName, meetingTypeRows]) => ({
        businessName,
        meetingTypes: (meetingTypeRows ?? [])
          .map((m) => m.meetingTypeName)
          .filter((name) => meetingTypes.includes(name))
      }))
      .filter((row) => businesses.includes(row.businessName));

    const csvBusinesses = (user.businessNameCSV ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter((name) => name.length > 0 && businesses.includes(name));
    const csvMeetingTypes = (user.meetingTypesNameCSV ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter((name) => name.length > 0 && meetingTypes.includes(name));
    const preloadedFromCsv: BusinessMeetingTypeMappingRow[] = csvBusinesses.map((businessName) => ({
      businessName,
      meetingTypes: csvMeetingTypes
    }));

    const preloadedMappings = preloadedFromMap.length > 0 ? preloadedFromMap : preloadedFromCsv;

    setMappings(
      preloadedMappings.length > 0
        ? preloadedMappings
        : [
            {
              businessName: "",
              meetingTypes: [...meetingTypes]
            }
          ]
    );
    setEmailLookupText(user.email ?? "");
    setBusinessError("");
    form.reset({
      userName: user.userName ?? "",
      email: user.email ?? "",
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      roleId: user.roleId?.toString() ?? ""
    });
  };

  const closeModal = () => {
    setEditing(null);
    setMappings([]);
    setEmailLookupText("");
    setBusinessError("");
    form.reset({
      userName: "",
      email: "",
      firstName: "",
      lastName: "",
      roleId: ""
    });
  };

  const onSubmit = form.handleSubmit(async (values) => {
    // Duplicate email check on create — mirrors Blazor:
    // if (Users.Any(u => u.Email?.EqualsExt(CreateRequest.Email)))
    const isCreate = !editing?.id || editing.id === 0;
    if (isCreate) {
      const duplicate = (users ?? []).some(
        (u) => u.email?.toLowerCase() === values.email.toLowerCase()
      );
      if (duplicate) {
        form.setError("email", { message: "A user with this email already exists." });
        return;
      }
    }

    // Business/meeting-type validation only when role requires it
    if (showBusinessSection) {
      if (mappings.length === 0) {
        setBusinessError("At least one business-to-meeting-type mapping is required.");
        return;
      }

      const emptyBusiness = mappings.some((m) => !m.businessName);
      if (emptyBusiness) {
        setBusinessError("Each mapping row must have a business selected.");
        return;
      }

      const emptyMeetingTypes = mappings.some((m) => m.meetingTypes.length === 0);
      if (emptyMeetingTypes) {
        setBusinessError("Select at least one meeting type in each mapping row.");
        return;
      }

      const uniqueBusinesses = new Set(mappings.map((m) => m.businessName));
      if (uniqueBusinesses.size !== mappings.length) {
        setBusinessError("Each business can be selected only once.");
        return;
      }
    }

    const roleId = parseInt(values.roleId, 10);
    const role = roles.find((r) => r.id === roleId);
    if (!role) {
      form.setError("roleId", { message: "Invalid role" });
      return;
    }

    // Transform selected businesses to API format (only when role requires it)
    const businessMap = showBusinessSection
      ? mappings.reduce(
          (acc, row) => {
            acc[row.businessName] = row.meetingTypes.map((meetingTypeName) => ({
              meetingTypeName
            }));
            return acc;
          },
          {} as Record<string, { meetingTypeName: string }[]>
        )
      : {};

    try {
      if (editing?.id && editing.id > 0) {
        await updateMutation.mutateAsync({
          id: editing.id,
          email: values.email,
          userName: values.userName,
          firstName: values.firstName,
          lastName: values.lastName,
          roleId: roleId,
          roleName: role.role,
          userBusinessMeetingTypeMap: showBusinessSection
            ? mappings.reduce(
                (acc, row) => {
                  acc[row.businessName] = row.meetingTypes.map((meetingTypeName) => ({
                    meetingTypeName,
                    isMapped: true
                  }));
                  return acc;
                },
                {} as Record<string, { meetingTypeName: string; isMapped: boolean }[]>
              )
            : {},
          dashboardPermissions: (currentUser?.specialPermissions ?? []).map((sp) => ({
            permissionId: sp.id,
            isActive: dashboardAccess[sp.componentKey] ?? false
          }))
        });
        toast.success("User updated successfully.");
      } else {
        await createMutation.mutateAsync({
          email: values.email,
          userName: values.userName,
          firstName: values.firstName,
          lastName: values.lastName,
          roleId: roleId,
          roleName: role.role,
          userBusinessMeetigtypeMaps: businessMap as Record<string, { meetingTypeName: string }[]>,
          dashboardPermissions: (currentUser?.specialPermissions ?? []).map((sp) => ({
            permissionId: sp.id,
            isActive: dashboardAccess[sp.componentKey] ?? false
          }))
        });
        toast.success("User created successfully.");
      }

      await queryClient.invalidateQueries({ queryKey: settingsKeys.users() });
      closeModal();
    } catch {
      toast.error("Unable to save user.");
    }
  });

  const onDelete = async (user: UserResponse) => {
    if (!user.email) {
      toast.error("User email is missing.");
      return;
    }

    if (user.isLoggedInUser) {
      toast.error("You cannot remove the logged-in user.");
      return;
    }

    const confirmed = window.confirm(`Delete user \"${user.userName ?? user.email}\"?`);
    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(user.email);
      toast.success("User removed successfully.");
      await queryClient.invalidateQueries({ queryKey: settingsKeys.users() });
    } catch {
      toast.error("Unable to remove user.");
    }
  };

  return (
    <section className="rounded border-slate-200 bg-white" >
      <div className="mb-4 flex flex-wrap items-center gap-3" >
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search"
          className="w-full max-w-md border border-slate-300 px-3 py-2 text-sm"
          aria-label="Search users"
        />

        <button
          type="button"
          onClick={openCreate}
          
          
          className="px-3 py-2 text-sm font-medium text-white bg-[#26890d] hover:bg-[#1f6609] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add User
        </button>
      </div>

      {isPending ? (
        <div className="py-8 text-sm text-slate-600">Loading users...</div>
      ) : (
        <div className=" overflow-x-auto overflow-y-auto" style={{ height: "calc(90vh - 150px)" }}>
          <table className="min-w-full border border-slate-200 text-left text-sm" >
            <thead className="sticky top-0 bg-[#E8E8E8] text-xs font-semibold text-black">
              <tr>
                <th className="border-b border-slate-200 p-0" aria-sort={getAriaSort("userName")}>
                  <SortableHeader label="User Name" sortDirection={sortState.userName} onClick={() => handleColumnSort("userName")} />
                </th>
                <th className="border-b border-slate-200 p-0" aria-sort={getAriaSort("email")}>
                  <SortableHeader label="Email Address" sortDirection={sortState.email} onClick={() => handleColumnSort("email")} />
                </th>
                <th className="border-b border-slate-200 p-0" aria-sort={getAriaSort("roleName")}>
                  <SortableHeader label="User Role" sortDirection={sortState.roleName} onClick={() => handleColumnSort("roleName")} />
                </th>
                <th className="border-b border-slate-200 p-0" aria-sort={getAriaSort("businessNameCSV")}>
                  <SortableHeader label="Business" sortDirection={sortState.businessNameCSV} onClick={() => handleColumnSort("businessNameCSV")} />
                </th>
                <th className="border-b border-slate-200 p-0" aria-sort={getAriaSort("meetingTypesNameCSV")}>
                  <SortableHeader label="Meeting Type(s)" sortDirection={sortState.meetingTypesNameCSV} onClick={() => handleColumnSort("meetingTypesNameCSV")} />
                </th>
                <th className="border-b border-slate-200 p-0" aria-sort={getAriaSort("createdDate")}>
                  <SortableHeader label="Date Added" sortDirection={sortState.createdDate} onClick={() => handleColumnSort("createdDate")} />
                </th>
                <th className="border-b border-slate-200 p-0" aria-sort={getAriaSort("lastLoginDate")}>
                  <SortableHeader label="Last Login" sortDirection={sortState.lastLoginDate} onClick={() => handleColumnSort("lastLoginDate")} />
                </th>
                <th className="border-b border-slate-200 px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((user, idx) => (
                <tr key={user.id} className={idx % 2 === 0 ? "bg-white" : "bg-[#F2F2F2]"}>
                  <td className="border-b border-slate-200 px-3 py-2">{user.userName ?? "-"}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{user.email ?? "-"}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{user.roleName ?? "-"}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{user.businessNameCSV || "-"}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{user.meetingTypesNameCSV || "-"}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{displayDate(user.createdDate)}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{displayDate(user.lastLoginDate)}</td>
                  <td className="border-b border-slate-200 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                       
                        onClick={() => openEdit(user)}
                        
                        
                        className="inline-flex h-8 w-8 items-center justify-center border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label={`Edit ${user.userName ?? user.email ?? "user"}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        
                        onClick={() => onDelete(user)}
                        
                        
                        className="inline-flex h-8 w-8 items-center justify-center border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label={`Delete ${user.userName ?? user.email ?? "user"}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {sortedUsers.length === 0 ? (
                <tr>
                  <td className="px-3 py-8 text-center text-slate-500" colSpan={8}>
                    No users available.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      {isFetching && !isPending ? <div className="mt-2 text-xs text-slate-500">Refreshing users...</div> : null}

      {editing ? (
        <div 
          className="fixed inset-0 z-50 flex bg-black/30" 
          role="dialog" 
          aria-modal="true" 
          aria-labelledby="user-modal-title"
          onClick={closeModal}
          onKeyDown={(e) => { if (e.key === "Escape") closeModal(); }}
        >
          <div 
            className="ml-auto flex w-96 flex-col border-l border-slate-300 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between bg-black p-4">
              <h3 id="user-modal-title" className="text-base font-semibold text-white">
                {editing.id > 0 ? "Edit User" : "Add User"}
              </h3>
              <button type="button" onClick={closeModal} className="text-white hover:text-slate-300" aria-label="Close dialog">
                <X className="h-4 w-4" />
              </button>
            </div>

            <form className="flex-1 space-y-3 overflow-y-auto p-4" id="user-form" onSubmit={onSubmit}>
              <div>
                <label htmlFor="user-email" className="mb-1 block text-sm font-medium text-slate-800">
                  Email Lookup
                </label>
                {editing.id > 0 ? (
                  <input
                    id="user-email"
                    type="email"
                    readOnly
                    className={cn(
                      "w-full border border-slate-300 px-3 py-2 text-sm bg-slate-50 text-slate-500",
                      form.formState.errors.email && "border-red-500"
                    )}
                    {...form.register("email")}
                  />
                ) : (
                  <EmailLookupAutocomplete
                    id="user-email"
                    value={emailLookupText}
                    suggestions={profileSuggestions ?? []}
                    isLoading={searchingProfiles}
                    error={form.formState.errors.email?.message}
                    onInputChange={(value) => {
                      setEmailLookupText(value);
                      form.setValue("email", value, { shouldDirty: true, shouldValidate: true });
                      const derivedUserName = value.includes("@") ? value.split("@")[0] : value;
                      form.setValue("userName", derivedUserName, { shouldDirty: true, shouldValidate: true });
                    }}
                    onSelect={(profile) => {
                      setEmailLookupText(profile.email);
                      form.setValue("email", profile.email, { shouldDirty: true, shouldValidate: true });
                      form.setValue("userName", profile.fullName, { shouldDirty: true, shouldValidate: true });
                      form.setValue("firstName", profile.firstName ?? "", { shouldDirty: true });
                      form.setValue("lastName", profile.lastName ?? "", { shouldDirty: true });
                    }}
                  />
                )}
                {form.formState.errors.email ? (
                  <p className="mt-1 text-xs text-red-600">{form.formState.errors.email.message}</p>
                ) : null}
              </div>

              <div>
                <label htmlFor="user-role" className="mb-1 block text-sm font-medium text-slate-800">
                  Role
                </label>
                <select
                  id="user-role"
                  className={cn("w-full border border-slate-300 px-3 py-2 text-sm", form.formState.errors.roleId && "border-red-500")}
                  {...form.register("roleId", {
                    onChange: () => {
                      setBusinessError("");
                    }
                  })}
                >
                  <option value="0">Select role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.role}
                    </option>
                  ))}
                </select>
                {form.formState.errors.roleId ? (
                  <p className="mt-1 text-xs text-red-600">{form.formState.errors.roleId.message}</p>
                ) : null}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-slate-900">Dashboard Access</h4>
                <div className="mt-2 flex flex-col gap-2">
                    {(currentUser?.specialPermissions ?? []).map((sp) => {
                      const dashboardName =
                        sp.componentKey === "CanViewDashboard"
                          ? "Draft Calendar Metrics Dashboard"
                          : sp.componentKey === "CanViewGRCDashboard"
                            ? "GRC Summary Dashboard"
                            : sp.componentKey;

                      return (
                        <label
                          key={sp.componentKey}
                          className="flex items-center gap-2 text-sm text-slate-800"
                        >
                          <input
                            type="checkbox"
                            checked={dashboardAccess[sp.componentKey] ?? false}
                            onChange={(e) =>
                              setDashboardAccess((prev) => ({
                                ...prev,
                                [sp.componentKey]: e.target.checked,
                              }))
                            }
                            className="h-4 w-4 rounded border-slate-300"
                          />

                          {dashboardName}
                        </label>
                      );
                    })}
              </div>
            </div>
            <hr className="mt-1 border-slate-200" />


              {showBusinessSection && (
                <BusinessMeetingTypeSelector
                  mappings={mappings}
                  onMappingsChange={(items) => {
                    setMappings(items);
                    setBusinessError("");
                  }}
                  businesses={businesses}
                  meetingTypes={meetingTypes}
                  error={businessError}
                />
              )}

            



              <div></div>
            </form>

            <div className="border-t border-slate-200 px-4 py-3">
              <div className="flex justify-end gap-2">
                <button type="button" onClick={closeModal} className="border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  form="user-form"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="inline-flex items-center gap-2 border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-60"
                >
                  {createMutation.isPending || updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
